"use client";

// A number that counts up to its value once, on mount — the dashboard's stat
// figures. The animation is the point of this hook and the animation is also
// its only hazard, so it is worth stating exactly what happens when.
//
// THE SERVER RENDERS THE REAL NUMBER. `useState(value)` means the SSR markup,
// the client's first paint and a visitor with no JavaScript all show the
// finished figure. Rendering a zero server-side and counting up on the client
// would be a hydration mismatch waiting to happen (the two renders would
// disagree on the text node) and would show a stranger "0 jobs" for a frame.
//
// THE REWIND HAPPENS IN AN EFFECT, NEVER IN RENDER. After mount the hook
// schedules `setDisplay(0)` on a timeout and the count on a second timeout —
// never a bare setState in the effect body, which the repo's
// `react-hooks/set-state-in-effect` rule forbids for exactly the reason it
// matters here: a synchronous write would land inside the same commit as the
// first paint and could flash.
//
// THE COUNT STARTS AFTER THE CARD'S ENTRANCE. `delayMs` is the caller's
// stagger plus the entrance duration, so the figure begins moving once the
// card it lives in has finished arriving rather than underneath it.
//
// requestAnimationFrame, not an interval: the number is a rendering concern
// and should tick with the display, and rAF stops on a backgrounded tab
// instead of burning a timer. `prefers-reduced-motion` skips the count
// entirely and leaves the final value on screen — the rule is "render the
// final state immediately", not "count faster".

import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/app/lib/use-reduced-motion";

const COUNT_MS = 500;

export function useCountUp(value: number, delayMs = 0): number {
  const reduced = usePrefersReducedMotion();
  const [display, setDisplay] = useState(value);
  const frame = useRef(0);

  useEffect(() => {
    let cancelled = false;

    if (reduced || value === 0) {
      // Nothing to animate. The deferred write still matters: if a previous
      // value left the display part-way through a count, this settles it.
      const settle = setTimeout(() => {
        if (!cancelled) setDisplay(value);
      }, 0);
      return () => {
        cancelled = true;
        clearTimeout(settle);
      };
    }

    // Rewind while the card is still invisible (its entrance starts at
    // opacity 0), so the jump from the final figure down to zero is never
    // on screen.
    const rewind = setTimeout(() => {
      if (!cancelled) setDisplay(0);
    }, 0);

    let from = 0;
    const tick = (now: number) => {
      if (cancelled) return;
      const elapsed = Math.min((now - from) / COUNT_MS, 1);
      // Cubic ease-out: fast off the mark, decelerating into the real figure.
      // A linear count reads as a progress bar; this reads as a number
      // arriving.
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setDisplay(Math.round(eased * value));
      if (elapsed < 1) frame.current = requestAnimationFrame(tick);
    };

    const begin = setTimeout(() => {
      if (cancelled) return;
      from = performance.now();
      frame.current = requestAnimationFrame(tick);
    }, Math.max(0, delayMs));

    return () => {
      cancelled = true;
      clearTimeout(rewind);
      clearTimeout(begin);
      cancelAnimationFrame(frame.current);
    };
  }, [value, delayMs, reduced]);

  return display;
}
